import type { AuthTokens, User } from '@/types';
import { openDB, type IDBPDatabase } from 'idb';

const DB_NAME = 'pdr-auth-db';
const DB_VERSION = 1;

interface AuthDB {
  auth: {
    key: string;
    value: AuthTokens | User;
  };
}

class TokenManager {
  private dbPromise: Promise<IDBPDatabase<AuthDB>>;

  constructor() {
    this.dbPromise = this.initDB();
  }

  private async initDB(): Promise<IDBPDatabase<AuthDB>> {
    return openDB<AuthDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('auth')) {
          db.createObjectStore('auth');
        }
      },
    });
  }

  /**
   * Save tokens to IndexedDB
   */
  async saveTokens(tokens: AuthTokens): Promise<void> {
    const db = await this.dbPromise;
    await db.put('auth', tokens, 'tokens');
  }

  /**
   * Get tokens from IndexedDB
   */
  async getTokens(): Promise<AuthTokens | null> {
    try {
      const db = await this.dbPromise;
      const tokens = await db.get('auth', 'tokens');
      return tokens as AuthTokens | null;
    } catch {
      return null;
    }
  }

  /**
   * Get just the access token (null if expired)
   */
  async getAccessToken(): Promise<string | null> {
    const tokens = await this.getTokens();
    if (!tokens || this.isTokenExpired(tokens)) {
      return null;
    }
    return tokens.accessToken;
  }

  /**
   * Check if token is expired (with 5 min buffer)
   */
  isTokenExpired(tokens: AuthTokens): boolean {
    return tokens.expiresAt < Date.now() + 5 * 60 * 1000;
  }

  /**
   * Save user info to IndexedDB
   */
  async saveUser(user: User): Promise<void> {
    const db = await this.dbPromise;
    await db.put('auth', user, 'user');
  }

  /**
   * Get user info from IndexedDB
   */
  async getUser(): Promise<User | null> {
    try {
      const db = await this.dbPromise;
      const user = await db.get('auth', 'user');
      return user as User | null;
    } catch {
      return null;
    }
  }

  /**
   * Clear all auth data
   */
  async clearAll(): Promise<void> {
    const db = await this.dbPromise;
    await db.delete('auth', 'tokens');
    await db.delete('auth', 'user');
  }

  /**
   * Get a valid token, or try to refresh via GIS silently.
   * Returns null if no valid token and refresh fails (user can reconnect).
   * NOTE: we no longer wipe the session here — that logged the user out.
   */
  async ensureValidToken(): Promise<string | null> {
    const tokens = await this.getTokens();
    if (!tokens) {
      return null;
    }

    if (!this.isTokenExpired(tokens)) {
      return tokens.accessToken;
    }

    // Token expired — try silent refresh via GIS
    try {
      const { googleAuthService } = await import('./googleAuth');
      const newTokenResponse = await googleAuthService.refreshToken();

      await this.saveTokens({
        accessToken: newTokenResponse.access_token,
        expiresAt: Date.now() + newTokenResponse.expires_in * 1000,
      });

      return newTokenResponse.access_token;
    } catch (error) {
      console.error('Silent token refresh failed:', error);
      return null;
    }
  }
}

export const tokenManager = new TokenManager();
